import React, { useState, useEffect } from "react";
import styles from "./style.module.css";
import { useLocation } from "react-router-dom";
import { linkReplaceFn } from "../../utils/custom_funtions";
import useMathjax from "../../hooks/useMathjax";
import { getOrGenerateQRCode } from "../../utils/qrCodeUtils";
import footerEnglish from "../../assets/img/footer2.jpg";
import footerHindi from "../../assets/img/black_footer.jpg";
import header from "../../assets/img/image.png"

const QuestionModuleRender = () => {
  const location = useLocation();
  const responseData = location.state?.responseData || [];
  const language = location.state?.language || "english";
  const paper = location.state?.paper || "";
  const [qrCodes, setQrCodes] = useState({});
  const [qrCodesLoading, setQrCodesLoading] = useState(true);


  // Re-typeset MathJax when questions or language change (covers options)
  useMathjax(responseData, language);
  console.log(responseData)


  // Generate QR codes for all questions
  useEffect(() => {
    const generateAllQRCodes = async () => {
      setQrCodesLoading(true);
      const qrCodeMap = {};

      for (const question of responseData) {
        const questionId = question.qid || question.id || question.question_id;
        if (questionId) {
          try {
            const qrCodeDataURL = await getOrGenerateQRCode(question, questionId);
            if (qrCodeDataURL) {
              qrCodeMap[questionId] = qrCodeDataURL;
            }
          } catch (error) {
            console.error(`Error generating QR code for question ${questionId}:`, error);
          }
        }
      }

      setQrCodes(qrCodeMap);
      setQrCodesLoading(false);
    };

    if (responseData.length > 0) {
      generateAllQRCodes();
    } else {
      setQrCodesLoading(false);
    }
  }, [responseData]);

  const setPrintPageSize = (size) => {
    let style = document.getElementById("dynamic-print-page-size");

    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-print-page-size";
      document.head.appendChild(style);
    }

    style.innerHTML = `
      @media print {
        @page {
          size: ${size};
          margin:.5in .6in .5in .6in !important;
          background-color:#fff !important;
          
        }
      }
    `;
  };

  useEffect(() => {
    // Bulk Question Request size
    setPrintPageSize("8.26in 10.1in");

    return () => {
      // Cleanup when leaving page
      const style = document.getElementById("dynamic-print-page-size");
      if (style) style.remove();
    };
  }, []);


  // Extract options from HTML string (preserve MathJax markup)
  const extractOptions = (htmlString) => {
    if (!htmlString) return [];
    return htmlString.split("@@@").map((html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.innerHTML.trim(); // Keep inline HTML for MathJax
    });
  };

  // Preserve the uploaded Excel/API row sequence. Group only by contiguous
  // exercise section; sub-topic headings are emitted later only when the value changes.
  const groupByExerciseInOrder = () => {
    const sections = [];
    let currentExerciseLevel = null;
    let currentSectionType = null;

    responseData.forEach((question) => {
      if (question.section) {
        currentExerciseLevel = question.section;
      }
      if (question.section_type) {
        currentSectionType = question.section_type;
      }

      const exerciseLevel = currentExerciseLevel || "Exercise-1(Level A)";
      const sectionType = currentSectionType || "Single Correct";
      const previousSection = sections[sections.length - 1];

      if (!previousSection || previousSection.exerciseLevel !== exerciseLevel) {
        sections.push({
          exerciseLevel,
          sectionType,
          questions: [question],
        });
      } else {
        previousSection.questions.push(question);
      }
    });

    return sections;
  };

  const orderedSections = groupByExerciseInOrder();

  const getSubTopicHeading = (question) =>
    question.sub_topics || question.sub_topic_name || "General";

  const usesNumericOptionLabels =
    paper === "neet" || responseData?.[0]?.sub_name === "Biology";

  // API answer_type_id: 3 = numerical, 4 = subjective/descriptive.
  // These types have no MCQ choices, even when `type` is not Integer-Type.
  // Only this explicitly identified section uses descriptive hints in its answer key.
  const isSubjectiveExerciseSection = (exerciseLevel, sectionType) =>
    String(exerciseLevel || "").trim().toLowerCase() === "exercise 3 (section-b)" &&
    String(sectionType || "").trim().toLowerCase() === "subjective type questions";

  const isNonChoiceQuestion = (question) =>
    [3, 4].includes(Number(question.answer_type_id)) ||
    question.type === "Integer-Type";

  const formatAnswerForDisplay = (rawValue, question) => {
    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return "";
    }

    const value = String(rawValue).trim();
    // Preserve numerical/subjective answers, including leading zeroes and decimals.
    if (isNonChoiceQuestion(question)) return value;
    const letterToNumeric = { A: "1", B: "2", C: "3", D: "4" };

    if (!usesNumericOptionLabels) {
      if (/^[1-4]$/.test(value)) {
        return ["A", "B", "C", "D"][Number(value) - 1] || value;
      }
      const index = Number(value);
      if (!Number.isNaN(index) && index >= 0 && index <= 3) {
        return ["A", "B", "C", "D"][index];
      }
      return value.toUpperCase();
    }

    if (letterToNumeric[value.toUpperCase()]) {
      return letterToNumeric[value.toUpperCase()];
    }
    if (/^[1-4]$/.test(value)) {
      return value;
    }
    const index = Number(value);
    if (!Number.isNaN(index) && index >= 0 && index <= 3) {
      return String(index + 1);
    }
    return value;
  };

  // Build option list for each question so layout can be managed easily
  const buildOptionList = (question) => {
    const optionLabels = usesNumericOptionLabels
      ? ["1", "2", "3", "4"]
      : ["A", "B", "C", "D"];

    const englishOptionChunks = extractOptions(question.eng_option);
    const hindiOptionChunks = extractOptions(question.hindi_option);

    const englishFallbacks = [
      question.option_a,
      question.option_b,
      question.option_c,
      question.option_d,
    ];

    const hindiFallbacks = [
      question.option_a_hindi,
      question.option_b_hindi,
      question.option_c_hindi,
      question.option_d_hindi,
    ];

    return optionLabels.map((label, idx) => ({
      label,
      value:
        language === "english"
          ? englishOptionChunks[idx] || englishFallbacks[idx]
          : hindiOptionChunks[idx] || hindiFallbacks[idx],
    }));
  };

  // Render option content with MathJax-ready HTML
  const renderOptionContent = (value) => {
    if (!value) return <span className={styles.optionText}>&nbsp;</span>;
    return (
      <span
        className={styles.optionText}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  };

  // Allow per-question option layout selection with stable class names
  const getOptionLayoutClass = (question) => {
    const layoutHint =
      question.optionLayout ||
      question.option_layout ||
      question.optionColumns ||
      question.option_columns;

    const normalized =
      typeof layoutHint === "string"
        ? layoutHint.toLowerCase()
        : layoutHint;

    if (normalized === 1 || normalized === "1" || normalized === "one" || normalized === "single") {
      return "option-cols-1";
    }

    if (normalized === 4 || normalized === "4" || normalized === "four" || normalized === "all") {
      return "option-cols-4";
    }

    if (normalized === 2 || normalized === "2" || normalized === "two") {
      return "option-cols-2";
    }

    // Module publishing defaults to one option per row unless explicitly overridden.
    return "option-cols-1";
  };

  const getDisplayNumber = (question, zeroBasedFallback) => {
    const excelOrApiNumber = Number(question?.display_number);
    if (Number.isInteger(excelOrApiNumber) && excelOrApiNumber > 0) {
      return excelOrApiNumber;
    }
    return zeroBasedFallback + 1;
  };

  // Render a single question
  const renderQuestion = (question, index, columnsTotal, type, globalIndex, subjectiveSection = false) => {
    const questionId = question.qid || question.id || question.question_id;
    const qrCodeDataURL = questionId ? qrCodes[questionId] : null;
    const fallbackIndex = globalIndex !== undefined ? globalIndex : index;
    const displayNumber = getDisplayNumber(question, fallbackIndex);
    const showOptions = !subjectiveSection && !isNonChoiceQuestion(question);
    const options = showOptions ? buildOptionList(question) : [];
    const optionLayoutClass = getOptionLayoutClass(question);

    return (
      <div key={`q-${questionId || displayNumber}-${fallbackIndex}`} className={`${styles.questionItem} ${styles.questionWithQr}`}>
        {/* Question Row */}
        <div className={styles.questionRow}>
          <div className={styles.questionNumber}>{displayNumber}.</div>
          <div
            className={styles.questionText}
            dangerouslySetInnerHTML={{
              __html: language === "english" ? question.cq_text : question.q_text_hindi
            }}
          />

          {/* QR Code - Show generated QR code or existing one */}
          <div className={styles.qrCode}>
            {qrCodesLoading ? (
              <div style={{
                width: '60px',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#666'
              }}>
                Loading...
              </div>
            ) : (qrCodeDataURL || question.qr_code) ? (
              <img
                src={qrCodeDataURL || question.qr_code}
                alt="QR Code"
                title={`Scan to view question ${questionId || displayNumber + 1}`}
              />
            ) : null}
          </div>
        </div>



        {/* Options Grid */}
        {showOptions && (
          <div
            className={`${styles.optionsGrid} option-grid ${optionLayoutClass}`}
            data-option-layout={optionLayoutClass}
          >
            {options.map((option) => (
              <div key={option.label} className={`${styles.option} option-item`}>
                <span className={styles.optionLabel}>({option.label})</span>
                {renderOptionContent(option.value)}
              </div>
            ))}
          </div>
        )}

        {/* Reference Citation */}
        <div className={styles.reference}>
          {(question.correct || question.incorrect) ? `{C: ${question.correct}%, I.C ${question.incorrect}%, U.A ${question.unattempted}%}` : ""} {question.examName && question.examYear ? `${question.examName} ${question.examYear}` : ""}
        </div>
      </div>
    );
  };

  // Function to get the answer value from different possible field names
  const getAnswerValue = (question) => {
    const possibleFields = [
      "answer",
      "correct_option",
      "correct_answer",
      "correct_ans",
      "correctAnswer",
    ];

    for (const field of possibleFields) {
      if (question[field] !== undefined && question[field] !== null) {
        return formatAnswerForDisplay(question[field], question);
      }
    }

    // `correct` is attempt statistics, not the correct answer.
    return "";
  };

  // Section-local question order is shared by print and answer keys.
  // Do not reset the counter when a different question type begins.
  const renderAllSectionAnswerKeys = () => {
    const allSectionAnswers = [];

    orderedSections.forEach(({ exerciseLevel, sectionType, questions }) => {
      if (!questions.length) return;

      const showHints = isSubjectiveExerciseSection(exerciseLevel, sectionType);
      let answerRows;

      if (showHints) {
        // Hint HTML is supplied by the API. Do not treat answer, solution or
        // attempt-statistics fields as substitutes for a missing hint.
        answerRows = questions.map((question, idx) => {
          const questionNumber = getDisplayNumber(question, idx);
          const questionId = question.qid || question.id || question.question_id;
          const hintHtml = typeof question.hint === "string" ? question.hint.trim() : "";
          const bodyMatch = hintHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          const hintContent = bodyMatch ? bodyMatch[1].trim() : hintHtml;
          const hasHint = Boolean(hintContent) && !/^<p>\s*<\/p>$/i.test(hintContent);
          return (
            <div key={`hint-${exerciseLevel}-${questionId || questionNumber}-${idx}`} className={styles.subjectiveHintItem}>
              <strong className={styles.answerNumber}>{questionNumber}.</strong>
              {hasHint ? (
                <div className={styles.subjectiveHintContent} dangerouslySetInnerHTML={{ __html: hintContent }} />
              ) : (
                <span className={styles.subjectiveHintMissing}>Hint not available</span>
              )}
            </div>
          );
        });
      } else {
        answerRows = [];
        for (let startIdx = 0; startIdx < questions.length; startIdx += 8) {
          answerRows.push(
            <div key={`row-${exerciseLevel}-${startIdx}`} className={styles.answerRow}>
              {questions.slice(startIdx, startIdx + 8).map((question, idx) => {
                const fallbackIndex = startIdx + idx;
                const questionNumber = getDisplayNumber(question, fallbackIndex);
                const questionId = question.qid || question.id || question.question_id;
                return (
                  <div key={`answer-${exerciseLevel}-${questionId || questionNumber}-${fallbackIndex}`} className={styles.answerItem}>
                    <strong className={styles.answerNumber}>{questionNumber}.</strong> <span>{getAnswerValue(question)}</span>
                  </div>
                );
              })}
            </div>
          );
        }
      }

      allSectionAnswers.push(
        <div key={exerciseLevel} className={styles.answerKeySection}>
          <h3 className={styles.answerKeyTitle}>{exerciseLevel}</h3>
          <div className={showHints ? styles.subjectiveHintList : styles.answerKeyContainer}>
            {answerRows}
          </div>
        </div>
      );
    });

    if (!allSectionAnswers.length) return null;
    return (
      <div className={styles.masterAnswerKeyContainer}>
        <h2 className={styles.masterAnswerKeyTitle}>Answer Keys</h2>
        {allSectionAnswers}
      </div>
    );
  };

  // The API supplies shared paragraph HTML on each linked question.
  // Print it once before the first question in each contiguous paragraph run.
  const renderQuestionsForPrint = (questions, type, startingIndex = 0, paragraphState, subjectiveSection = false) => {
    const content = [];
    questions.forEach((question, idx) => {
      const paragraphId = question.paragraph_id ?? question.matrix_paragraph_id;
      const paragraphHtml = language === "hindi"
        ? (question.p_text_hindi || question.m_p_text_hindi || question.p_text || question.m_p_text)
        : (question.p_text || question.m_p_text);

      if (paragraphId != null && paragraphHtml && paragraphState.lastId !== String(paragraphId)) {
        content.push(
          <div
            key={`paragraph-${startingIndex + idx}-${paragraphId}`}
            className={`${styles.questionText} ${styles.paragraphBlock}`}
            style={{ breakInside: "avoid", pageBreakInside: "avoid", margin: "10px 0 8px" }}
          >
            <div className={styles.paragraphHeading}>Paragraph :</div>
            <div dangerouslySetInnerHTML={{ __html: paragraphHtml }} />
          </div>
        );
      }
      paragraphState.lastId = paragraphId == null ? null : String(paragraphId);
      content.push(renderQuestion(question, startingIndex + idx, questions.length, type, startingIndex + idx, subjectiveSection));
    });
    return content;
  };

  const renderSectionInExcelOrder = (questions, sectionType, paragraphState, subjectiveSection = false) => {
    const content = [];
    let previousSubTopic = null;

    questions.forEach((question, index) => {
      const subTopic = getSubTopicHeading(question);

      if (subTopic !== previousSubTopic) {
        content.push(
          <div key={`subtopic-${index}-${subTopic}`} className={styles.subjectName}>
            {subTopic}
          </div>
        );
        previousSubTopic = subTopic;
      }

      content.push(
        ...renderQuestionsForPrint([question], sectionType, index, paragraphState, subjectiveSection)
      );
    });

    return content;
  };

  const footerImage = language === "hindi" ? footerHindi : footerEnglish;

  return (
    <div className={`${styles.container} container-fluid ${styles.printable} print-page print-bulk-question-request`}>
      {/* Blank page at the start */}
      <div className={styles.blankPage}></div>

      <div className={styles.printHeader}>
        <img src={header} alt="Header" className={styles.headerImage} />
      </div>
      <div className={styles.printContent}>

        {/* Questions Section */}
        {orderedSections.map(({ exerciseLevel, sectionType, questions }) => {
          const paragraphState = { lastId: null };
          const sectionContent = renderSectionInExcelOrder(
            questions,
            sectionType,
            paragraphState,
            isSubjectiveExerciseSection(exerciseLevel, sectionType)
          );

          return (
            <div key={exerciseLevel} className={styles.exerciseSection}>
              <div className={styles.redLine} />
              <div className={styles.exerciseTitleRow}>
                <div className={styles.exerciseTitle}>{exerciseLevel}</div>
                <div className={styles.questionType}>
                  {sectionType || "Single Correct"}
                </div>
              </div>

              <div className={styles.redLine1} />

              <div className={styles.printOptimizedLayout} style={{ columnCount: 2 }}>
                {sectionContent}
              </div>
            </div>
          );
        })}

        {/* All Answer Keys at the bottom */}
        {renderAllSectionAnswerKeys()}
      </div>

      {/* Print Footer */}
      <div className={styles.printFooter}>

        <img
          src={footerImage}
          alt="Footer"
          style={{ width: "100%", height: "35px", }}
        />
      </div>

    </div>
  );
};

export default QuestionModuleRender;